import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, X, Fuel, Anchor, Wrench, Heart, HelpCircle, Lock, Users } from 'lucide-react';

const DISTRESS_TYPES = [
  { id: 'FUEL', label: 'Fuel issue', icon: Fuel, color: 'bg-amber-500' },
  { id: 'DISABLED', label: 'Disabled / drifting', icon: Anchor, color: 'bg-blue-500' },
  { id: 'MECHANICAL', label: 'Mechanical problem', icon: Wrench, color: 'bg-orange-500' },
  { id: 'MEDICAL', label: 'Medical emergency', icon: Heart, color: 'bg-red-500' },
  { id: 'OTHER', label: 'Other distress', icon: HelpCircle, color: 'bg-gray-500' },
];

const SOSModal = ({ 
  isOpen, 
  onClose, 
  onTriggerSOS, 
  onCancelSOS,
  isPaid = false,
  isLoading = false,
  activeSOS = null 
}) => {
  const [step, setStep] = useState('type'); // 'type', 'confirm', 'sending', 'sent'
  const [selectedType, setSelectedType] = useState(null);
  const [sosMode, setSOSMode] = useState('PUBLIC'); // 'PUBLIC' or 'PRIVATE'
  const [canceling, setCanceling] = useState(false);

  if (!isOpen) return null;

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    // Paid users get to choose public/private, free users go directly to confirm
    if (isPaid) {
      setStep('mode');
    } else {
      setSOSMode('PUBLIC');
      setStep('confirm');
    }
  };

  const handleModeSelect = (mode) => {
    setSOSMode(mode);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setStep('sending');
    try {
      await onTriggerSOS(selectedType, sosMode);
      setStep('sent');
    } catch (error) {
      console.error('SOS failed:', error);
      setStep('type');
    }
  };

  const handleCancelSOS = async () => {
    if (!onCancelSOS || !activeSOS) {
      console.error('Cancel SOS: missing handler or activeSOS', { onCancelSOS: !!onCancelSOS, activeSOS });
      return;
    }
    setCanceling(true);
    try {
      // Determine which SOS to cancel
      const sosId = activeSOS.public_sos?.id || activeSOS.private_sos?.id;
      const sosType = activeSOS.public_sos ? 'PUBLIC' : 'PRIVATE';
      console.log('Canceling SOS:', { sosId, sosType, activeSOS });
      if (!sosId) {
        console.error('Cancel SOS: No SOS ID found in activeSOS');
        return;
      }
      await onCancelSOS(sosId, sosType);
      handleClose();
    } catch (error) {
      console.error('Cancel SOS failed:', error);
    } finally {
      setCanceling(false);
    }
  };

  const handleClose = () => {
    setStep('type');
    setSelectedType(null);
    setSOSMode('PUBLIC');
    onClose();
  };

  // If user has active SOS, show status instead
  if (activeSOS) {
    return (
      <div className="fixed inset-0 bg-black/60 z-[2000] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl animate-in zoom-in-95">
          <CardContent className="p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-700 mb-2">SOS Active</h2>
              <p className="text-gray-600 mb-4">
                {activeSOS.public_sos ? 'Public SOS is active. Nearby boaters using Boat 360 can see your distress signal.' : 'Your friends have been notified via the Boat 360 app.'}
              </p>

              {activeSOS.public_sos && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-500">
                    <strong>{activeSOS.public_sos.helper_count || 0}</strong> helpers responding
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Expires in ~{Math.ceil((new Date(activeSOS.public_sos.expires_at) - new Date()) / 60000)} minutes
                  </p>
                </div>
              )}

              {/* Critical safety reminder while SOS is active */}
              <div
                className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-left mb-4"
                data-testid="sos-active-reminder"
              >
                <p className="text-xs font-bold text-red-800 mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Need real emergency help?
                </p>
                <p className="text-xs text-red-900 leading-snug">
                  Call <strong>911</strong> or hail the <strong>U.S. Coast Guard on VHF Channel 16</strong>.
                  Boat 360 does not contact emergency services.
                </p>
              </div>

              <div className="flex gap-3 justify-center">
                <Button
                  variant="destructive"
                  onClick={handleCancelSOS}
                  className="flex-1"
                  data-testid="cancel-sos-btn"
                  disabled={canceling}
                >
                  {canceling ? 'Canceling...' : 'Cancel SOS'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[2000] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white shadow-2xl animate-in zoom-in-95">
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-red-600 text-white p-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                <h2 className="text-lg font-bold">Emergency SOS</h2>
              </div>
              <button 
                onClick={handleClose}
                className="p-1 hover:bg-red-500 rounded"
                data-testid="close-sos-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Persistent Safety Disclaimer - shown on every step */}
          <div
            className="bg-amber-50 border-y-2 border-amber-400 px-4 py-3"
            data-testid="sos-safety-disclaimer"
          >
            <div className="flex gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 leading-snug">
                <p className="font-bold mb-1">This is NOT a 911 service.</p>
                <p>
                  Boat 360 only notifies other boaters using this app — it does
                  <strong> NOT </strong>contact 911, the Coast Guard, or any emergency
                  responders. For life-threatening emergencies, <strong>call 911</strong> or
                  hail the <strong>U.S. Coast Guard on VHF Channel 16</strong> immediately.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4">
            {/* Step 1: Select Distress Type */}
            {step === 'type' && (
              <div>
                <p className="text-gray-600 mb-4 text-sm">
                  Select the type of emergency:
                </p>
                <div className="space-y-2">
                  {DISTRESS_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => handleTypeSelect(type.id)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 hover:border-red-300 hover:bg-red-50 transition-all"
                      data-testid={`sos-type-${type.id.toLowerCase()}`}
                    >
                      <div className={`w-10 h-10 ${type.color} rounded-full flex items-center justify-center`}>
                        <type.icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-medium text-gray-800">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Select Mode (Paid users only) */}
            {step === 'mode' && (
              <div>
                <p className="text-gray-600 mb-4 text-sm">
                  Choose who should see your SOS:
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => handleModeSelect('PRIVATE')}
                    className="w-full flex items-center gap-3 p-4 rounded-lg border-2 border-cyan-200 bg-cyan-50 hover:border-cyan-400 transition-all"
                    data-testid="sos-mode-private"
                  >
                    <div className="w-12 h-12 bg-cyan-500 rounded-full flex items-center justify-center">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-cyan-800">Friends Only</p>
                      <p className="text-xs text-cyan-600">Notify your friends with your exact location and identity</p>
                    </div>
                    <Badge className="bg-cyan-500">Recommended</Badge>
                  </button>
                  
                  <button
                    onClick={() => handleModeSelect('PUBLIC')}
                    className="w-full flex items-center gap-3 p-4 rounded-lg border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all"
                    data-testid="sos-mode-public"
                  >
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-gray-800">Public Anonymous</p>
                      <p className="text-xs text-gray-500">Alert all boaters within 5 miles (anonymous, expires in 30 min)</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 'confirm' && (
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  Send {sosMode === 'PRIVATE' ? 'Private' : 'Public'} SOS?
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  {sosMode === 'PRIVATE' 
                    ? 'Your friends will receive an urgent notification with your exact location.' 
                    : 'Nearby boaters within 5 miles will see an anonymous distress marker for 30 minutes.'}
                </p>
                <Badge className={sosMode === 'PRIVATE' ? 'bg-cyan-500' : 'bg-orange-500'}>
                  {DISTRESS_TYPES.find(t => t.id === selectedType)?.label}
                </Badge>
                
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setStep('type')}
                    className="flex-1"
                    disabled={isLoading}
                  >
                    Back
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleConfirm}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    disabled={isLoading}
                    data-testid="confirm-send-sos"
                  >
                    {isLoading ? 'Sending...' : 'SEND SOS'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Sending */}
            {step === 'sending' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Sending SOS...</p>
              </div>
            )}

            {/* Step 5: Sent */}
            {step === 'sent' && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-green-700 mb-2">SOS Sent to Boaters</h3>
                <p className="text-gray-600 mb-4 text-sm">
                  {sosMode === 'PRIVATE'
                    ? 'Your friends have been notified and can see your location.'
                    : 'Nearby boaters using Boat 360 can now see your distress signal.'}
                </p>

                {/* Critical reminder - emergency services NOT contacted */}
                <div
                  className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-left mb-4"
                  data-testid="sos-sent-reminder"
                >
                  <p className="text-xs font-bold text-red-800 mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Life-threatening emergency?
                  </p>
                  <p className="text-xs text-red-900 leading-snug">
                    Call <strong>911</strong> or hail the <strong>U.S. Coast Guard on VHF Channel 16</strong> now.
                    Boat 360 only alerts nearby app users — not emergency responders.
                  </p>
                </div>

                <Button onClick={handleClose} className="w-full">
                  Done
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SOSModal;
